import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useRouteAccess, Roles } from '@/app/Roles/useRouteAccess';
import { useUserPreference } from '@/hooks/useUserPreference';
import {
  ONBOARDING_KEYS,
  startOnboarding,
  hasCompletedOnboarding,
  syncOnboardingFromBackend,
  hasWelcomeDismissed,
} from '@/lib/onboardingTours/driver';

interface OnboardingConfig {
  onboardingKey: keyof typeof ONBOARDING_KEYS;
  pathMatch: (path: string) => boolean;
  selectors: string[];
  delay?: number;
  requiresRole?: Roles;
  customCheck?: (selectors: string[]) => boolean;
  tourName: string;
}

const onboardingConfigs: OnboardingConfig[] = [
  {
    onboardingKey: 'alertDetail',
    pathMatch: (path) => path.startsWith('/alert/'),
    selectors: [
      '[data-onboarding="alert-header-tabs"]',
      '[data-onboarding="alert-info-panel"]',
      '[data-onboarding="alert-snapshot-viewer"]',
      '[data-onboarding="alert-workflow-panel"]',
    ],
    delay: 1000,
    requiresRole: Roles.Monitoring,
    tourName: 'alert detail',
  },
  {
    onboardingKey: 'monitoringHome',
    pathMatch: (path) => path === '/' || path === '/home' || path === '/dashboard',
    selectors: [
      '[data-onboarding="home-welcome"]',
      '[data-onboarding="nav-bar"]',
      '[data-onboarding="nav-actions"]',
      '[data-onboarding="nav-hamburger"]',
      '[data-onboarding="home-options"]',
      '[data-onboarding="monitoring-menu"]',
    ],
    delay: 750,
    requiresRole: Roles.Monitoring,
    tourName: 'monitoring onboarding (home)',
  },
  {
    onboardingKey: 'monitoringAlerts',
    pathMatch: (path) => path === '/monitor',
    selectors: ['[data-onboarding="alert-tabs"]', '[data-onboarding="new-alerts"]'],
    delay: 750,
    requiresRole: Roles.Monitoring,
    tourName: 'monitoring onboarding (monitor alerts)',
  },
  {
    onboardingKey: 'adminAlerts',
    pathMatch: (path) => path === '/alerts',
    selectors: [
      '[data-onboarding="admin-alerts-dashboard"]',
      '[data-onboarding="admin-alerts-column-toggle"]',
      '[data-onboarding="admin-alerts-filters-panel"]',
    ],
    delay: 750,
    tourName: 'admin alerts',
  },
  {
    onboardingKey: 'adminAlertDetail',
    pathMatch: (path) => path.startsWith('/monitoring/alert/'),
    selectors: [
      '[data-onboarding="admin-alert-details"]',
      '[data-onboarding="admin-alert-actions"]',
      '[data-onboarding="admin-alert-events"]',
    ],
    delay: 1000,
    tourName: 'admin alert detail',
  },
  {
    onboardingKey: 'viewer',
    pathMatch: (path) => path === '/viewer',
    selectors: [
      '[data-onboarding="viewer-toolbar"]',
      '[data-onboarding="viewer-hierarchy"]',
      '[data-onboarding="viewer-detail"]',
    ],
    delay: 1000,
    tourName: 'viewer',
  },
  {
    onboardingKey: 'customers',
    pathMatch: (path) => path === '/customers',
    selectors: [
      '[data-onboarding="customers-page"]',
      '[data-onboarding="customers-add-button"]',
      '[data-onboarding="customers-table"]',
    ],
    delay: 750,
    tourName: 'customers',
  },
  {
    onboardingKey: 'customerDetail',
    pathMatch: (path) => /^\/customers\/[^/]+$/.test(path),
    selectors: [
      '[data-onboarding="customer-detail-header"]',
      '[data-onboarding="customer-detail-content"]',
    ],
    delay: 1000,
    tourName: 'customer detail',
  },
  {
    onboardingKey: 'sites',
    pathMatch: (path) => path === '/sites',
    selectors: [
      '[data-onboarding="sites-page"]',
      '[data-onboarding="sites-add-button"]',
      '[data-onboarding="sites-table"]',
    ],
    delay: 750,
    tourName: 'sites',
  },
  {
    onboardingKey: 'siteAddNew',
    pathMatch: (path) => path === '/site/new' || /^\/site\/new/.test(path),
    selectors: [
      '[data-onboarding="site-add-tabs"]',
      '[data-onboarding="site-add-customer-select"]',
      '[data-onboarding="site-add-name"]',
    ],
    delay: 1000,
    customCheck: (selectors) => {
      const tabs = document.querySelector(selectors[0]);
      const customerSelect = document.querySelector(selectors[1]);
      const name = document.querySelector(selectors[2]);
      return !!(tabs && (customerSelect || name));
    },
    tourName: 'site add new',
  },
  {
    onboardingKey: 'siteDetail',
    pathMatch: (path) =>
      /^\/[Cc]ustomer\/[^/]+\/project\/[^/]+$/.test(path) || /^\/site\/[^/]+$/.test(path),
    selectors: [
      '[data-onboarding="site-detail-header"]',
      '[data-onboarding="site-detail-tabs"]',
      '[data-onboarding="site-detail-content"]',
    ],
    delay: 2000,
    tourName: 'site detail',
  },
  {
    onboardingKey: 'devices',
    pathMatch: (path) => path === '/devices',
    selectors: [
      '[data-onboarding="devices-page"]',
      '[data-onboarding="devices-add-button"]',
      '[data-onboarding="devices-table"]',
    ],
    delay: 750,
    tourName: 'devices',
  },
  {
    onboardingKey: 'addDevice',
    pathMatch: (path) =>
      path === '/devices/add-device' || /^\/devices\/customer\/[^/]+\/add-device/.test(path),
    selectors: [
      '[data-onboarding="add-device-header"]',
      '[data-onboarding="add-device-sidebar"]',
      '[data-onboarding="add-device-details-section"]',
    ],
    delay: 2000,
    tourName: 'add device',
  },
  {
    onboardingKey: 'deviceEdit',
    pathMatch: (path) => /^\/devices\/[^/]+\/edit$/.test(path),
    selectors: [
      '[data-onboarding="device-edit-header"]',
      '[data-onboarding="device-edit-tabs"]',
      '[data-onboarding="device-edit-content"]',
    ],
    delay: 2000,
    tourName: 'device edit',
  },
  {
    onboardingKey: 'connections',
    pathMatch: (path) => path === '/connections',
    selectors: [
      '[data-onboarding="connections-page"]',
      '[data-onboarding="connections-add-button"]',
      '[data-onboarding="connections-table"]',
    ],
    delay: 750,
    tourName: 'connections',
  },
  {
    onboardingKey: 'connectionDetail',
    pathMatch: (path) => /^\/connections\/[^/]+$/.test(path),
    selectors: [
      '[data-onboarding="connection-detail-header"]',
      '[data-onboarding="connection-detail-tabs"]',
      '[data-onboarding="connection-detail-form"]',
    ],
    delay: 2000,
    tourName: 'connection detail',
  },
  {
    onboardingKey: 'users',
    pathMatch: (path) => path === '/users',
    selectors: [
      '[data-onboarding="users-page"]',
      '[data-onboarding="users-add-button"]',
      '[data-onboarding="users-table"]',
    ],
    delay: 750,
    tourName: 'users',
  },
  {
    onboardingKey: 'addUser',
    pathMatch: (path) => path === '/users/add',
    selectors: [
      '[data-onboarding="add-user-header"]',
      '[data-onboarding="add-user-basic-info"]',
      '[data-onboarding="add-user-role-type"]',
    ],
    delay: 2000,
    tourName: 'add user',
  },
  {
    onboardingKey: 'userDetail',
    pathMatch: (path) => /^\/users\/[^/]+$/.test(path) && path !== '/users/add',
    selectors: [
      '[data-onboarding="user-detail-info-card"]',
      '[data-onboarding="user-detail-sidebar"]',
      '[data-onboarding="user-detail-basic-info"]',
    ],
    delay: 2000,
    tourName: 'user detail',
  },
  {
    onboardingKey: 'teams',
    pathMatch: (path) => path === '/team',
    selectors: [
      '[data-onboarding="teams-page"]',
      '[data-onboarding="teams-add-button"]',
      '[data-onboarding="teams-table"]',
    ],
    delay: 750,
    tourName: 'teams',
  },
  {
    onboardingKey: 'addTeam',
    pathMatch: (path) => path === '/team/add',
    selectors: ['[data-onboarding="add-team-form"]'],
    delay: 2000,
    tourName: 'add team',
  },
  {
    onboardingKey: 'teamDetail',
    pathMatch: (path) => /^\/tenant\/[^/]+\/team\/[^/]+$/.test(path),
    selectors: ['[data-onboarding="team-detail-header"]', '[data-onboarding="team-detail-tabs"]'],
    delay: 1000,
    tourName: 'team detail',
  },
  {
    onboardingKey: 'alertAudit',
    pathMatch: (path) => path === '/alert-audit',
    selectors: ['[data-onboarding="alert-audit-form"]'],
    delay: 750,
    tourName: 'alert audit',
  },
];

function isHomepage(path: string): boolean {
  return path === '/' || path === '/home' || path === '/dashboard';
}

function checkElementsExist(
  selectors: string[],
  customCheck?: (selectors: string[]) => boolean
): boolean {
  if (customCheck) {
    return customCheck(selectors);
  }

  return selectors.every((selector) => !!document.querySelector(selector));
}

export function OnboardingTrigger() {
  const location = useLocation();
  const { user, roles } = useAuthStore();
  const { hasRoleAccess } = useRouteAccess();
  const hasTriggeredRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 10;
  
  // Track welcome modal dismissal state from localStorage cache
  const [welcomeModalDismissed, setWelcomeModalDismissed] = useState(false);

  // Check if onboarding is required for this user
  // - New users: set to 'true' when created by backend
  // - Existing users: not set, defaults to 'false'
  // - Users who dismissed/completed: set to 'false'
  const { value: onboardingRequired, isLoading: isLoadingPreference } = useUserPreference({
    key: 'onboarding_required',
    defaultValue: 'false',
  });

  // Check welcome modal dismissal state and listen for changes
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const dismissed = hasWelcomeDismissed(user.id);
    setWelcomeModalDismissed(dismissed);

    const handleWelcomeModalDismissed = () => {
      setWelcomeModalDismissed(true);
    };

    window.addEventListener('onboarding-welcome-dismissed', handleWelcomeModalDismissed);

    return () => {
      window.removeEventListener('onboarding-welcome-dismissed', handleWelcomeModalDismissed);
    };
  }, [user?.id]);

  // Sync onboarding state from backend on mount (single JSON blob fetch)
  useEffect(() => {
    if (!user?.id || isLoadingPreference) {
      return;
    }

    let isMounted = true;

    const syncOnboarding = async () => {
      try {
        await syncOnboardingFromBackend(user.id);
      } catch (error) {
         
        const isAborted =
          (error as any)?.code === 'ECONNABORTED' ||
          (error as any)?.message === 'Request aborted';
        if (!isAborted && isMounted) {
          console.warn('[Onboarding] Failed to sync state from backend:', error);
        }
      }
    };

    syncOnboarding();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isLoadingPreference]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    if (isLoadingPreference) {
      return;
    }

    if (onboardingRequired !== 'true') {
      return;
    }

    if (isHomepage(location.pathname) && !welcomeModalDismissed) {
      return;
    }

    hasTriggeredRef.current = false;
    retryCountRef.current = 0;

    const matchingConfig = onboardingConfigs.find((config) => {
      const isOnPage = config.pathMatch(location.pathname);
      const hasCompleted = hasCompletedOnboarding(ONBOARDING_KEYS[config.onboardingKey], user.id);
      const hasRolePermission = !config.requiresRole || hasRoleAccess(config.requiresRole);

      return isOnPage && !hasCompleted && hasRolePermission;
    });

    if (!matchingConfig) {
      return;
    }

    const checkElements = () => {
      return checkElementsExist(matchingConfig.selectors, matchingConfig.customCheck);
    };

    const tryStartOnboarding = () => {
      if (checkElements()) {
        hasTriggeredRef.current = true;

        startOnboarding(user.id, location.pathname, roles);
      } else if (retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        setTimeout(tryStartOnboarding, 500);
      } else {
        console.warn(
          `[Onboarding] Max retries reached, ${matchingConfig.tourName} elements not found`
        );
      }
    };

    const delay = matchingConfig.delay ?? 1000;
    const timer = setTimeout(tryStartOnboarding, delay);

    return () => clearTimeout(timer);
  }, [location.pathname, user?.id, hasRoleAccess, onboardingRequired, isLoadingPreference, welcomeModalDismissed, roles]);

  return null;
}